/**
 * DemoDirectorInsights — Insights tab content for the Demo Director modal.
 *
 * Thin orchestrator (~250 lines). Three view modes, controlled by
 * local state:
 *   • Default: overview — totals, funnel, experiments, sparkline,
 *     leaderboard, dead-templates list, generated-at timestamp.
 *   • Drill-down: per-template engagement timeline (visit log,
 *     notification log, Re-engage button) — owned by
 *     `EngagementTimelineView` which itself can swap to
 *     `ReengagePanel`.
 *   • Batch re-engage: when the parent passes `batchReengageIds`
 *     (from a digest deep-link), the entire panel is replaced with
 *     `BatchReengageView` until the SE backs out.
 *
 * Heavy components extracted to `./insights/` for sanity:
 *   • _shared.jsx                       — EngagementBadge, InsightStat,
 *                                          FunnelStat, formatRelative,
 *                                          formatDuration
 *   • _angles.jsx                       — A/B angle helpers + hooks
 *   • SubjectLineExperimentsPanel.jsx
 *   • EngagementTimelineView.jsx
 *   • ReengagePanel.jsx
 *   • BatchReengageView.jsx
 *
 * Re-exports `EngagementBadge` and `InsightStat` so existing
 * consumers (e.g. PEDiagnosticPage) keep importing from the same
 * path with no changes.
 */
import React, { useState } from 'react';
import { Button } from '../ui/button';
import {
  Loader2, RotateCw, ChevronRight, BellRing, Sparkles,
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import api from '../../lib/api';
import {
  EngagementBadge, InsightStat, FunnelStat,
  formatRelative,
} from './insights/_shared';
import { SubjectLineExperimentsPanel } from './insights/SubjectLineExperimentsPanel';
import { EngagementTimelineView } from './insights/EngagementTimelineView';
import { BatchReengageView } from './insights/BatchReengageView';

// Re-export for backwards compatibility with existing importers.
export { EngagementBadge, InsightStat };

export const DemoDirectorInsights = ({ insights, loading, onRefresh, batchReengageIds, onBatchReengageConsumed }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [batchIds, setBatchIds] = useState(null);
  const [funnel, setFunnel] = useState(null);

  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const res = await api.get('/admin/demo-director/reengage-funnel?window_days=30');
        if (!cancelled) setFunnel(res.data);
      } catch (_e) { /* additive — never block */ }
    };
    run();
    return () => { cancelled = true; };
  }, []);

  React.useEffect(() => {
    if (batchReengageIds && batchReengageIds.length > 0) {
      setBatchIds(batchReengageIds);
      onBatchReengageConsumed?.();
    }
  }, [batchReengageIds, onBatchReengageConsumed]);

  if (batchIds && batchIds.length > 0) {
    return (
      <BatchReengageView
        templateIds={batchIds}
        onBack={() => setBatchIds(null)}
      />
    );
  }

  if (selectedTemplate) {
    return (
      <EngagementTimelineView
        templateId={selectedTemplate.id}
        templateName={selectedTemplate.name}
        onBack={() => setSelectedTemplate(null)}
      />
    );
  }

  if (loading && !insights) {
    return (
      <div className="flex items-center justify-center py-16 text-xs text-slate-500" data-testid="dd-insights-loading">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading insights…
      </div>
    );
  }
  if (!insights) {
    return (
      <div className="py-16 text-center text-xs text-slate-500" data-testid="dd-insights-empty">
        No insights data yet.
        <Button size="sm" variant="ghost" onClick={onRefresh} className="ml-2 text-fuchsia-600">
          <RotateCw className="h-3 w-3 mr-1" /> Retry
        </Button>
      </div>
    );
  }

  const t = insights.totals || {};
  const sparkline = insights.visits_by_day || [];
  const leaderboard = insights.leaderboard || [];
  const dead = insights.dead_templates || [];

  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1" data-testid="dd-insights-panel">
      {/* Totals */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <InsightStat label="Templates" value={t.templates ?? 0} testId="dd-insights-stat-templates" />
        <InsightStat label="Shared" value={t.templates_shared ?? 0} testId="dd-insights-stat-shared" />
        <InsightStat label="With opens" value={t.templates_with_opens ?? 0} testId="dd-insights-stat-with-opens" />
        <InsightStat label="Visits (7d)" value={t.recent_visits_7d ?? 0} highlight testId="dd-insights-stat-visits-7d" />
      </div>

      {/* Re-engage funnel — only renders once at least one draft has
          been generated, otherwise it's just noise. */}
      {funnel && (funnel.totals.drafts_generated > 0 || funnel.totals.emails_sent > 0) && (
        <div className="rounded-lg border border-rose-100 bg-rose-50/30 p-3" data-testid="dd-insights-funnel">
          <div className="flex items-baseline justify-between mb-2 flex-wrap gap-1">
            <h4 className="text-xs font-semibold text-rose-800 flex items-center gap-1">
              <Sparkles className="h-3 w-3" /> Re-engage funnel — last 30 days
            </h4>
            <p className="text-[10px] text-rose-600">
              "Returned" = template received fresh visit within 7d after send
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <FunnelStat label="Drafts" value={funnel.totals.drafts_generated} testId="dd-funnel-stat-drafts" />
            <FunnelStat
              label="Sends" value={funnel.totals.emails_sent}
              hint={
                funnel.totals.emails_actually_dispatched < funnel.totals.emails_sent
                  ? `${funnel.totals.emails_actually_dispatched} actually dispatched`
                  : null
              }
              testId="dd-funnel-stat-sends"
            />
            <FunnelStat
              label="Returned" value={funnel.totals.templates_that_returned}
              testId="dd-funnel-stat-returned"
            />
            <FunnelStat
              label="Conversion"
              value={
                funnel.totals.conversion_pct_send_to_return == null
                  ? '—' : `${funnel.totals.conversion_pct_send_to_return}%`
              }
              highlight
              testId="dd-funnel-stat-conversion"
            />
          </div>
        </div>
      )}

      {/* A/B subject-line experiments — auto-hides when no sends. */}
      <SubjectLineExperimentsPanel />

      {/* 14-day visit sparkline */}
      <div className="rounded-lg border border-slate-100 bg-white p-3" data-testid="dd-insights-sparkline">
        <div className="mb-2 flex items-baseline justify-between">
          <div>
            <h4 className="text-xs font-semibold text-slate-700">Prospect visits — last 14 days</h4>
            <p className="text-[10px] text-slate-400">From <code>demo_director_audit.share_url_visit</code></p>
          </div>
          <Button
            size="sm" variant="ghost"
            onClick={onRefresh} disabled={loading}
            className="h-6 text-[10px] text-slate-500 hover:text-fuchsia-600"
            data-testid="dd-insights-refresh-btn"
          >
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCw className="h-3 w-3" />}
            <span className="ml-1">Refresh</span>
          </Button>
        </div>
        {sparkline.length > 0 ? (
          <ResponsiveContainer width="100%" height={120}>
            <LineChart data={sparkline} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={(v) => v?.slice(5)} interval={1} />
              <YAxis tick={{ fontSize: 9 }} allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="visits" stroke="#a855f7" strokeWidth={2} dot={{ r: 2 }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-20 flex items-center justify-center text-[11px] text-slate-400">No visits yet</div>
        )}
      </div>

      {/* Leaderboard — clickable rows */}
      <div className="rounded-lg border border-slate-100 bg-white p-3" data-testid="dd-insights-leaderboard">
        <h4 className="text-xs font-semibold text-slate-700 mb-2">Top templates by opens</h4>
        {leaderboard.length === 0 ? (
          <p className="text-[11px] text-slate-400 py-3">No template has been opened yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {leaderboard.map((row, i) => (
              <li
                key={row.id}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedTemplate({ id: row.id, name: row.name })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelectedTemplate({ id: row.id, name: row.name });
                  }
                }}
                className="flex items-center justify-between gap-2 py-1.5 text-[11px] cursor-pointer hover:bg-slate-50 -mx-1 px-1 rounded transition-colors"
                data-testid={`dd-insights-leaderboard-row-${i}`}
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="w-4 text-slate-400 tabular-nums">{i + 1}</span>
                  <span className="font-medium text-slate-800 truncate">{row.name}</span>
                  {row.industry_preset && (
                    <span className="text-[9px] uppercase tracking-wider text-slate-400">{row.industry_preset}</span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-none">
                  {row.engagement && (
                    <EngagementBadge
                      engagement={row.engagement}
                      testId={`dd-insights-leaderboard-row-${i}-engagement`}
                    />
                  )}
                  <span className="text-emerald-600 font-semibold tabular-nums">{row.share_open_count}× opens</span>
                  <span className="text-slate-400 tabular-nums">{row.use_count} applies</span>
                  {row.last_notified_at && (
                    <span
                      className="text-[9px] text-fuchsia-600 inline-flex items-center gap-0.5"
                      title={`Last alert sent ${formatRelative(row.last_notified_at)}`}
                      data-testid={`dd-insights-leaderboard-row-${i}-last-alert`}
                    >
                      <BellRing className="h-2.5 w-2.5" />
                      {formatRelative(row.last_notified_at)}
                    </span>
                  )}
                  <ChevronRight className="h-3 w-3 text-slate-300 group-hover:text-slate-500" />
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Dead templates */}
      <div className="rounded-lg border border-amber-100 bg-amber-50/40 p-3" data-testid="dd-insights-dead-templates">
        <h4 className="text-xs font-semibold text-amber-800 mb-2">
          Share URL minted, never opened
          <span className="ml-2 text-[10px] font-normal text-amber-600">({dead.length})</span>
        </h4>
        {dead.length === 0 ? (
          <p className="text-[11px] text-amber-700/70">All shared templates have been opened by a prospect at least once.</p>
        ) : (
          <ul className="space-y-1">
            {dead.map((row) => (
              <li key={row.id} className="text-[11px] text-amber-800 flex justify-between gap-2" data-testid={`dd-insights-dead-row-${row.id}`}>
                <span className="truncate">{row.name}</span>
                <span className="text-[10px] text-amber-600 flex-none">
                  {row.industry_preset || '—'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className="text-[10px] text-slate-400 text-right" data-testid="dd-insights-generated-at">
        Generated {insights.generated_at ? formatRelative(insights.generated_at) : '—'}
      </p>
    </div>
  );
};
